import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useRef, useState } from 'react';
import { BottomSheetContentLayout, MobileSummaryCard, TextField, Typography, useDevice, useOverlayContext } from 'seagull';
import { MobileTextFieldTID } from 'seagull/components/TextField/BaseTextField/BaseTextField.utils';
import { useInsertVariableIntoTextField } from '~/components/AgreementStudioPage/TemplateVariables/hooks/useInsertVariableIntoTextField';
import { VariableFieldAdornment } from '~/components/AgreementStudioPage/TemplateVariables/VariableFieldAdornment/VariableFieldAdornment';
import { VariablePickerPopover } from '~/components/VariablePickerPopover/VariablePickerPopover';
import { useLocaleKeys } from '~/i18n';
import styles from './NotificationEditField.module.scss';
export function NotificationEditField(props) {
    const { isMobileDevice } = useDevice();
    if (isMobileDevice && !props.disabled) return /*#__PURE__*/ _jsx(MobileField, _object_spread({}, props));
    return /*#__PURE__*/ _jsx(DesktopField, _object_spread({}, props));
}
function DesktopField(param) {
    let { label, value, onChange, placeholder, error, disabled, multiline, minRows, bi, testId, adornmentTestId, adornmentClassName, pickerTestId, customVariables, allowedSystemKeys } = param;
    const inputRef = useRef(null);
    const adornmentRef = useRef(null);
    const [pickerAnchor, setPickerAnchor] = useState(null);
    const insertVariable = useInsertVariableIntoTextField(inputRef, onChange);
    const handleSelectVariable = useCallback((variableLabel)=>{
        insertVariable(variableLabel);
        setPickerAnchor(null);
    }, [
        insertVariable
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(TextField, {
                label: label,
                value: value,
                onChange: (event)=>onChange(event.target.value),
                placeholder: placeholder,
                error: error,
                disabled: disabled,
                multiline: multiline,
                minRows: minRows,
                bi: bi,
                testId: testId,
                inputRef: inputRef,
                InputProps: disabled ? undefined : {
                    endAdornment: /*#__PURE__*/ _jsx("span", {
                        ref: adornmentRef,
                        className: adornmentClassName,
                        children: /*#__PURE__*/ _jsx(VariableFieldAdornment, {
                            testId: adornmentTestId,
                            onClick: ()=>setPickerAnchor(adornmentRef.current)
                        })
                    })
                }
            }),
            /*#__PURE__*/ _jsx(VariablePickerPopover, {
                anchorEl: pickerAnchor,
                customVariables: customVariables,
                allowedSystemKeys: allowedSystemKeys,
                allowCreate: false,
                onSelect: handleSelectVariable,
                onClose: ()=>setPickerAnchor(null),
                testId: pickerTestId
            })
        ]
    });
}
function MobileField(props) {
    const { pushBottomSheetPage } = useOverlayContext();
    const { label, value, error, testId } = props;
    return /*#__PURE__*/ _jsx(MobileSummaryCard, {
        testId: testId,
        items: [
            {
                title: label,
                titleTestId: MobileTextFieldTID.Label(testId),
                body: /*#__PURE__*/ _jsx(Typography, {
                    ellipsis: true,
                    testId: MobileTextFieldTID.Value(testId),
                    skin: error ? 'error' : 'standard',
                    children: value
                }),
                actions: [
                    {
                        type: 'edit',
                        testId: MobileTextFieldTID.EditButton(testId),
                        bi: {
                            name: 'edit_notification_field'
                        },
                        onClick: ()=>{
                            pushBottomSheetPage({
                                content: /*#__PURE__*/ _jsx(NotificationEditFieldBottomSheet, _object_spread({}, props))
                            });
                        }
                    }
                ]
            }
        ]
    });
}
function NotificationEditFieldBottomSheet(param) {
    let { label, value, onChange, placeholder, error, multiline, minRows, bi, testId, adornmentTestId, adornmentClassName, pickerTestId, customVariables, allowedSystemKeys, validateInBottomSheet } = param;
    var _ref;
    const { t } = useLocaleKeys('pages.notificationSettings.contentCustomization');
    const { hideBottomSheet } = useOverlayContext();
    const [tempValue, setTempValue] = useState(value);
    const inputRef = useRef(null);
    const adornmentRef = useRef(null);
    const [pickerAnchor, setPickerAnchor] = useState(null);
    const insertVariable = useInsertVariableIntoTextField(inputRef, setTempValue);
    const handleSelectVariable = useCallback((variableLabel)=>{
        insertVariable(variableLabel);
        setPickerAnchor(null);
    }, [
        insertVariable
    ]);
    const validation = validateInBottomSheet === null || validateInBottomSheet === void 0 ? void 0 : validateInBottomSheet(tempValue);
    const handleSave = ()=>{
        if (tempValue !== value) onChange(tempValue);
        hideBottomSheet();
    };
    return /*#__PURE__*/ _jsxs(BottomSheetContentLayout, {
        contentClassName: styles.bottomSheetContent,
        footer: {
            primaryAction: {
                text: t.saveButton(),
                onClick: handleSave,
                bi: {
                    name: 'save_notification_field_sheet'
                }
            }
        },
        children: [
            /*#__PURE__*/ _jsx(TextField, {
                collapseHelperText: true,
                label: label,
                value: tempValue,
                onChange: (event)=>setTempValue(event.target.value),
                placeholder: placeholder,
                multiline: multiline,
                minRows: minRows,
                error: (_ref = validation === null || validation === void 0 ? void 0 : validation.error) !== null && _ref !== void 0 ? _ref : error,
                helperText: validation === null || validation === void 0 ? void 0 : validation.helperText,
                bi: bi,
                testId: testId,
                inputRef: inputRef,
                InputProps: {
                    endAdornment: /*#__PURE__*/ _jsx("span", {
                        ref: adornmentRef,
                        className: adornmentClassName,
                        children: /*#__PURE__*/ _jsx(VariableFieldAdornment, {
                            testId: adornmentTestId,
                            onClick: ()=>setPickerAnchor(adornmentRef.current)
                        })
                    })
                }
            }),
            /*#__PURE__*/ _jsx(VariablePickerPopover, {
                anchorEl: pickerAnchor,
                customVariables: customVariables,
                allowedSystemKeys: allowedSystemKeys,
                allowCreate: false,
                onSelect: handleSelectVariable,
                onClose: ()=>setPickerAnchor(null),
                testId: pickerTestId
            })
        ]
    });
}
